import uniq from 'lodash/uniq';
import { annotationSvgBand } from '@d3fc/d3fc-annotation';
import gridlines from 'utils/components/TimeSeriesChart/gridlines';
import { seriesSvgMulti, AnySeries } from '@d3fc/d3fc-series';

import { line } from 'utils/components/TimeSeriesChart/line';
import { PortfolioReturnsDataItem } from './portfolioReturnsData';
import { PortfolioReturnsConfigurationState } from '../store/configuration/PortfolioReturnsConfigurationState';

import { PortfolioReturnsComponentState } from '../store/component/PortfolioReturnsComponentState';
import { timeUtils } from '@instinet-analytics/core-lib';

const { roundToDays, addDays } = timeUtils;

export const portfolioReturnsSeries = (yTicks: number) => {
    const createLineSeries = (l: keyof PortfolioReturnsDataItem) =>
        line(
            (d: PortfolioReturnsDataItem) => d[l] as number,
            'line line-' + l,
            (d: PortfolioReturnsDataItem) => d.endOfBucketTime
        );

    const totalBpsValueSeries = createLineSeries('totalBpsValue');
    const buyBpsValueSeries = createLineSeries('buyBpsValue');
    const sellBpsValueSeries = createLineSeries('sellBpsValue');
    const shortBpsValueSeries = createLineSeries('shortBpsValue');
    const index1BpsValueSeries = createLineSeries('index1BpsValue');
    const index2BpsValueSeries = createLineSeries('index2BpsValue');

    const dayAnnotationSeries = getDayAnnotationBandSeries();

    return seriesSvgMulti()
        .series([
            dayAnnotationSeries,
            gridlines(yTicks, true),
            totalBpsValueSeries,
            buyBpsValueSeries,
            sellBpsValueSeries,
            shortBpsValueSeries,
            index1BpsValueSeries,
            index2BpsValueSeries,
        ])
        .mapping(
            (
                data: PortfolioReturnsDataItem[],
                index: number,
                series: AnySeries
            ) => {
                switch (series[index]) {
                    case dayAnnotationSeries:
                        return calculateDayAnnotationBands(data);
                    case totalBpsValueSeries:
                        return data.filter(
                            (d) => d.totalBpsValue !== undefined
                        );
                    case buyBpsValueSeries:
                        return data.filter((d) => d.buyBpsValue !== undefined);
                    case sellBpsValueSeries:
                        return data.filter((d) => d.sellBpsValue !== undefined);
                    case shortBpsValueSeries:
                        return data.filter(
                            (d) => d.shortBpsValue !== undefined
                        );
                    case index1BpsValueSeries:
                        return data.filter(
                            (d) => d.index1BpsValue !== undefined
                        );
                    case index2BpsValueSeries:
                        return data.filter(
                            (d) => d.index2BpsValue !== undefined
                        );
                    default:
                        return data;
                }
            }
        );
};

export function getLines(
    combineSellsAndShorts: boolean,
    config: PortfolioReturnsConfigurationState,
    componentState: PortfolioReturnsComponentState,
    multipleSides: boolean
) {
    const lines: (keyof PortfolioReturnsDataItem)[] = [];

    const hideTotal =
        config.hideTotal || config.invertSellLine || !multipleSides;
    !hideTotal && lines.push('totalBpsValue');

    !config.hideBuy && lines.push('buyBpsValue');
    !config.hideSell && lines.push('sellBpsValue');
    if (!combineSellsAndShorts && !config.hideShort) {
        lines.push('shortBpsValue');
    }
    if (config.index1 && !config.hideIndex1) {
        lines.push('index1BpsValue');
    }
    if (config.index2 && !config.hideIndex2) {
        lines.push('index2BpsValue');
    }
    return lines;
}

export interface PortfolioReturnsDayAnnotationBand {
    fromTime: number;
    toTime: number;
}
export function getDayAnnotationBandSeries() {
    return annotationSvgBand()
        .orient('vertical')
        .fromValue((d: PortfolioReturnsDayAnnotationBand) => d.fromTime)
        .toValue((d: PortfolioReturnsDayAnnotationBand) => d.toTime) // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .decorate((selection: any) => {
            selection.classed('day-annotation-bar', true);
        });
}

export function calculateDayAnnotationBands(
    timeSeries: PortfolioReturnsDataItem[]
): PortfolioReturnsDayAnnotationBand[] {
    const timeSeriesDates = uniq(timeSeries.map((t) => roundToDays(t.time)))
        .sort()
        .reverse();
    const ret: PortfolioReturnsDayAnnotationBand[] = [];
    for (let i = 0; i < timeSeriesDates.length; i++) {
        if (i % 2 === 1) {
            // Shade the odd dates ie day before latest, then every 2 after that
            const fromTime = timeSeriesDates[i];
            const toTime = addDays(fromTime, 1) - 1;
            ret.push({ fromTime, toTime });
        }
    }
    return ret;
}
